import {
  AccessibilityTagDto,
  CreateDeliveryZoneRequest,
  CreateGlobalOpeningHoursExceptionRequest,
  CreateLegalEntityRequest,
  CreateLegalEntityResponse,
  CreateOpeningHoursExceptionRequest,
  DeleteGlobalOpeningHoursExceptionRequest,
  DeleteOpeningHoursExceptionRequest,
  DeliveryZoneDto,
  EmptyResponse,
  EcomLiveSalesReportResponse,
  EcomSalesReportRequest,
  EcomSalesReportResponse,
  GetAllRestaurantExternalSystemsResponse,
  GetAllRestaurantsRestaurantDto,
  GetGlobalOpeningHoursExceptionDto,
  GetOpeningHoursDto,
  GetOpeningHoursExceptionDto,
  GetRestaurantExternalSystemsResponse,
  GetRestaurantLegalEntityResponse,
  GetTagsPriceffTagDto,
  LegalEntityDto,
  Point,
  ProductPriceGroupEnum,
  PublicHolidayPricingDate,
  RestaurantDetailsDto,
  RestaurantLegalEntityDto,
  RestaurantStatusDto,
  RestaurantWithNightSaleDetailsDto,
  RphRestaurantInformation,
  SendRestaurantDeliveryZoneToExternalSystemsRequest,
  ShopAccessibilityDto,
  ShopsApiClient,
  UpdateDeliveryZoneRequest,
  UpdateGlobalOpeningHoursExceptionRequest,
  UpdateLegalEntityRequest,
  UpdateLegalEntityResponse,
  UpdateRestaurantTemporaryStatusRequest,
  UpdateShopAccessibilityRequest,
  UpsertOpeningHoursRequest,
  UpsertOpeningHoursResponse,
  UpsertRestaurantStatusRequest,
  UpsertShopBaseMenuRequest,
  UserRolesDto,
  WebshopShopDto,
} from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import { getConfiguration } from '../shared/utils/configuration';
import { getRequestHeaders, postToSlack } from './utils';
import Axios from 'axios';
import { deliveryZoneState } from '../views/DeliveryZonesPageV2/utils/types';
import { uniqBy } from 'lodash';
import { MutationFunction, useQuery, UseQueryResult } from '@tanstack/react-query';
import { getShopsApiError } from './shops-api.errors';
import { ApiMutationOptions, ApiMutationUiOptions, useApiMutation } from './useApiMutation';
import dayjs from 'dayjs';
import { DatePickerFormat } from '../views/CampaignsPage/shared';

export const shopsApiQueryKeys = {
  allBackofficeRestaurants: 'all-backoffice-restaurant',
  allExceptionDates: 'exception-dates',
  allRestaurantExternalSystems: 'all-restaurant-external-systems',
  allRestaurants: 'restaurants',
  allRestaurantsWithDetails: 'restaurantsWithDetails',
  globalExceptionHours: 'global-exception-hours',
  singleBackofficeRestaurant: 'backoffice-restaurant',
  singleRestaurantExceptionHours: 'single-restaurant-exception-hours',
  singleRestaurantExternalSystems: 'single-restaurant-external-systems',
  singleRestaurantHours: 'single-restaurant-hours',
  singleRestaurantStatus: 'single-restaurant-status',
  allLegalEntities: 'legal-entities',
  allRestaurantLegalEntities: 'restaurant-legal-entities',
  restaurantLegalEntity: 'restaurant-legal-entity',
  ecomSalesReport: 'ecom-sales-report',
  ecomLiveSalesReport: 'ecom-live-sales-report',
  accessibilityTags: 'accessibility-tags',
  shopAccessibility: 'shop-accessibility',
};

export type PriceffTag = GetTagsPriceffTagDto & { color: string };

export const accessibilityTagCategories = {
  entrance: 'entrance',
  wc: 'wc',
} as const;

const DEFAULT_TIMEOUT_MS = 10000;

// The EcomSalesReport query can be slow on the backend, so it needs a longer
// timeout than the default used by every other request.
const ECOM_SALES_REPORT_TIMEOUT_MS = 120000;

// One client is cached per timeout value so that the default 10s timeout keeps
// applying to all regular requests while slow endpoints can opt into a longer one.
const clientsByTimeout = new Map<number, ShopsApiClient>();

const getApiClient = (timeout = DEFAULT_TIMEOUT_MS): ShopsApiClient => {
  const cached = clientsByTimeout.get(timeout);
  if (cached) return cached;

  const baseUrl = getConfiguration<string>('shopsApi.root', '');

  const instance = Axios.create({
    timeout,
    transformResponse: [],
  });

  instance.interceptors.request.use(async (config) => {
    const headers = await getRequestHeaders();
    config.headers = headers;
    return config;
  });

  const client = new ShopsApiClient(baseUrl, instance);
  clientsByTimeout.set(timeout, client);
  return client;
};

type Options<T> = Parameters<typeof useQuery<T>>['2'];

const getDefaultOptions = <T,>(): Options<T> => ({
  enabled: true,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
});

export const formatRestaurantName = (name: string): string => {
  if (name.toLowerCase().includes('kotipizza')) return name;

  return `Kotipizza ${name}`;
};

const fetchRestaurants = async (includeKotipizzaPrefix: boolean): Promise<WebshopShopDto[]> => {
  const client = getApiClient();
  const data = await client.getAllShops();

  const restaurants: WebshopShopDto[] = data.map((r) => ({
    ...r,
    displayName: includeKotipizzaPrefix ? formatRestaurantName(r.displayName) : r.displayName,
  }));

  restaurants.sort((a, b) => a.displayName.localeCompare(b.displayName));

  return restaurants;
};

export const useAllRestaurants = (
  includeKotipizzaPrefix = false,
  options = getDefaultOptions<WebshopShopDto[]>()
): UseQueryResult<WebshopShopDto[], unknown> =>
  useQuery<WebshopShopDto[]>(
    [shopsApiQueryKeys.allRestaurants],
    () => fetchRestaurants(includeKotipizzaPrefix),
    options
  );

const fetchAccessibilityTags = async (): Promise<AccessibilityTagDto[]> => {
  const client = getApiClient();
  return client.getAllAccessibilityTags();
};

export const useAccessibilityTags = (
  options = getDefaultOptions<AccessibilityTagDto[]>()
): UseQueryResult<AccessibilityTagDto[], unknown> =>
  useQuery<AccessibilityTagDto[]>([shopsApiQueryKeys.accessibilityTags], fetchAccessibilityTags, {
    ...options,
    retry: false,
  });

const fetchShopAccessibility = async (shopId: string): Promise<ShopAccessibilityDto> => {
  const client = getApiClient();
  return client.getShopAccessibilityInformationForShop(shopId);
};

export const useShopAccessibility = (
  shopId: string,
  options = getDefaultOptions<ShopAccessibilityDto>()
): UseQueryResult<ShopAccessibilityDto, unknown> =>
  useQuery<ShopAccessibilityDto>(
    [shopsApiQueryKeys.shopAccessibility, shopId],
    () => fetchShopAccessibility(shopId),
    { ...options, retry: false }
  );

export const updateShopAccessibility = (
  shopId: string,
  request: UpdateShopAccessibilityRequest
): Promise<ShopAccessibilityDto> => {
  const client = getApiClient();
  return client.putShopAccessibilityInformationForShop(shopId, request);
};

const fetchBackofficeRestaurants = async (): Promise<RphRestaurantInformation[]> => {
  const client = getApiClient();
  return client.getAllBackofficeRestaurants();
};

const fetchBackofficeRestaurant = async (shopExternalId: string): Promise<RphRestaurantInformation> => {
  const client = getApiClient();
  return client.getBackofficeRestaurantByExternalId(shopExternalId);
};

export const useBackofficeRestaurant = (
  shopExternalId: string,
  options = getDefaultOptions<RphRestaurantInformation>()
): UseQueryResult<RphRestaurantInformation, unknown> =>
  useQuery<RphRestaurantInformation>(
    [shopsApiQueryKeys.singleBackofficeRestaurant, shopExternalId],
    () => fetchBackofficeRestaurant(shopExternalId),
    options
  );

export const useAllBackofficeRestaurants = (
  options = getDefaultOptions<RphRestaurantInformation[]>()
): UseQueryResult<RphRestaurantInformation[], unknown> =>
  useQuery<RphRestaurantInformation[]>(
    [shopsApiQueryKeys.allBackofficeRestaurants],
    fetchBackofficeRestaurants,
    options
  );

export const getRestaurantStatus = (shopExternalId: string): Promise<RestaurantStatusDto> => {
  const client = getApiClient();
  return client.getRestaurantStatus(shopExternalId);
};

export const useRestaurantStatus = (shopExternalId: string): UseQueryResult<RestaurantStatusDto> => {
  return useQuery<RestaurantStatusDto>(
    [shopsApiQueryKeys.singleRestaurantStatus, shopExternalId],
    () => getRestaurantStatus(shopExternalId),
    { retry: 1 }
  );
};

export const updateRestaurantStatus = (request: UpsertRestaurantStatusRequest): Promise<RestaurantStatusDto> => {
  const client = getApiClient();
  return client.upsertRestaurantStatus(request);
};

export const updateRestaurantTemporaryStatus = (
  request: UpdateRestaurantTemporaryStatusRequest
): Promise<RestaurantStatusDto> => {
  const client = getApiClient();
  return client.upsertRestaurantTemporaryStatus(request);
};

export const updateRestaurantBaseProductMenu = (
  shopExternalId: string,
  baseMenuId: number
): Promise<UpsertShopBaseMenuRequest> => {
  const client = getApiClient();
  return client.upsertShopBaseMenu({ shopExternalId, baseMenuId });
};

export const getAllRestaurantDetails = async (): Promise<GetAllRestaurantsRestaurantDto[]> => {
  const client = getApiClient();
  return await client.getAllRestaurantsEndpoint();
};

export const useAllRestaurantsWithDetails = (): UseQueryResult<GetAllRestaurantsRestaurantDto[]> => {
  return useQuery<GetAllRestaurantsRestaurantDto[]>([shopsApiQueryKeys.allRestaurantsWithDetails], () =>
    getAllRestaurantDetails()
  );
};

export const getRestaurantDetailsById = async (shopId: number): Promise<RestaurantDetailsDto> => {
  const client = getApiClient();
  const response = await client.getRestaurantDetailsByIdsEndpoint([shopId]);
  if (response.length < 1) throw new Error(`Shop with id: ${shopId} not found from Shops API.`);

  return response[0];
};

const getRestaurantOpeningHours = async (shopExternalId: string): Promise<GetOpeningHoursDto[]> => {
  const client = getApiClient();
  const response = await client.getOpeningHours(shopExternalId);
  return response.openingHours;
};

export const useRestaurantOpeningHours = (shopExternalId: string): UseQueryResult<GetOpeningHoursDto[]> => {
  return useQuery<GetOpeningHoursDto[]>([shopsApiQueryKeys.singleRestaurantHours, shopExternalId], () =>
    getRestaurantOpeningHours(shopExternalId)
  );
};

export const updateOpeningHours = (request: UpsertOpeningHoursRequest): Promise<UpsertOpeningHoursResponse> => {
  const client = getApiClient();
  return client.updateOpeningHours(request);
};

const getRestaurantExceptionHours = async (shopExternalId: string): Promise<GetOpeningHoursExceptionDto[]> => {
  const client = getApiClient();
  const response = await client.getOpeningHoursExceptions(shopExternalId);
  return response.exceptions;
};

export const useRestaurantExceptionHours = (shopExternalId: string): UseQueryResult<GetOpeningHoursExceptionDto[]> => {
  return useQuery<GetOpeningHoursExceptionDto[]>(
    [shopsApiQueryKeys.singleRestaurantExceptionHours, shopExternalId],
    () => getRestaurantExceptionHours(shopExternalId)
  );
};

export const addRestaurantOpeningHoursException = async (request: CreateOpeningHoursExceptionRequest) => {
  const client = getApiClient();
  return client.createOpeningHoursException(request);
};

export const deleteRestaurantOpeningHoursException = async (request: DeleteOpeningHoursExceptionRequest) => {
  const client = getApiClient();
  return client.deleteOpeningHoursException(request);
};

const getGlobalExceptionHours = async (): Promise<GetGlobalOpeningHoursExceptionDto[]> => {
  const client = getApiClient();
  const response = await client.getGlobalOpeningHoursExceptions();
  return response.globalExceptions;
};

export const useGlobalExceptionHours = (): UseQueryResult<GetGlobalOpeningHoursExceptionDto[]> => {
  return useQuery<GetGlobalOpeningHoursExceptionDto[]>(
    [shopsApiQueryKeys.globalExceptionHours],
    getGlobalExceptionHours
  );
};

export const addGlobalExceptionHours = async (request: CreateGlobalOpeningHoursExceptionRequest) => {
  const client = getApiClient();
  client.createGlobalOpeningHoursException(request);
};

export const deleteGlobalExceptionHours = async (request: DeleteGlobalOpeningHoursExceptionRequest) => {
  const client = getApiClient();
  client.deleteGlobalOpeningHoursException(request);
};

export const updateGlobalExceptionHours = async (request: UpdateGlobalOpeningHoursExceptionRequest) => {
  const client = getApiClient();
  client.updateGlobalOpeningHoursException(request);
};

const getAllRestaurantExternalSystems = async (): Promise<GetAllRestaurantExternalSystemsResponse> => {
  const client = getApiClient();
  return client.getAllRestaurantExternalSystems();
};

export const useAllRestaurantExternalSystems = (): UseQueryResult<GetAllRestaurantExternalSystemsResponse> => {
  return useQuery([shopsApiQueryKeys.allRestaurantExternalSystems], () => getAllRestaurantExternalSystems(), {
    retry: false,
  });
};

const getAllLegalEntities = () => {
  const client = getApiClient();
  return client.getAllLegalEntities();
};

export const useAllLegalEntities = (): UseQueryResult<LegalEntityDto[]> => {
  return useQuery([shopsApiQueryKeys.allLegalEntities], getAllLegalEntities);
};

const getAllRestaurantLegalEntities = () => {
  const client = getApiClient();
  return client.getAllRestaurantsLegalEntities();
};

export const useAllRestaurantLegalEntities = (): UseQueryResult<RestaurantLegalEntityDto[]> => {
  return useQuery([shopsApiQueryKeys.allRestaurantLegalEntities], getAllRestaurantLegalEntities);
};

export const updateLegalEntity = (
  legalEntityId: number,
  request: UpdateLegalEntityRequest
): Promise<UpdateLegalEntityResponse> => {
  const client = getApiClient();
  return client.updateLegalEntity(legalEntityId, request);
};

export const createLegalEntity = (request: CreateLegalEntityRequest): Promise<CreateLegalEntityResponse> => {
  const client = getApiClient();
  return client.createLegalEntity(request);
};

const getRestaurantLegalEntity = (shopExternalId: string): Promise<GetRestaurantLegalEntityResponse> => {
  const client = getApiClient();
  return client.getRestaurantLegalEntity(shopExternalId);
};

export const useRestaurantLegalEntity = (shopExternalId: string): UseQueryResult<GetRestaurantLegalEntityResponse> => {
  return useQuery(
    [shopsApiQueryKeys.restaurantLegalEntity, shopExternalId],
    () => getRestaurantLegalEntity(shopExternalId),
    {
      retry: false,
    }
  );
};

export const linkRestaurantToLegalEntity = (shopExternalId: string, legalEntityId: number): Promise<EmptyResponse> => {
  const client = getApiClient();
  return client.linkRestaurantToLegalEntity({
    shopExternalId,
    legalEntityId,
  });
};

const getRestaurantExternalSystemsForShop = async (
  shopExternalId: string
): Promise<GetRestaurantExternalSystemsResponse> => {
  const client = getApiClient();
  return client.getRestaurantExternalSystems(shopExternalId);
};

export const useRestaurantExternalSystemsForShop = (
  shopExternalId: string
): UseQueryResult<GetRestaurantExternalSystemsResponse> => {
  return useQuery<GetRestaurantExternalSystemsResponse>(
    [shopsApiQueryKeys.singleRestaurantExternalSystems, shopExternalId],
    () => getRestaurantExternalSystemsForShop(shopExternalId),
    { retry: false }
  );
};

export const linkRestaurantToFoodora = async (
  shopExternalId: string,
  foodoraVendorId: string,
  foodoraPriceGroup: ProductPriceGroupEnum
) => {
  const client = getApiClient();
  await client.linkShopToFoodora({ shopExternalId, foodoraVendorId, foodoraPriceGroup });

  postToSlack(
    `Restaurant ${shopExternalId} linked to Foodora with id: ${foodoraVendorId}, price group: ${foodoraPriceGroup}.`,
    'restaurant-updates'
  );
};

export const createNewDeliveryZone = (request: CreateDeliveryZoneRequest) => {
  const client = getApiClient();
  postToSlack(`Delivery zone V2 ${request.deliveryZoneName} created`, 'restaurant-updates');
  return client.createDeliveryZone(request);
};

export const getAllDeliveryZones = async (): Promise<DeliveryZone[]> => {
  const client = getApiClient();
  const data = await client.getAllDeliveryZones();

  if (!data.deliveryZones) {
    return [];
  }

  return data.deliveryZones.map((zone) => {
    return {
      ...zone,
      zone: JSON.parse(zone.zoneJson ?? '{}') as { coordinates: Point[][] },
      state: deliveryZoneState.UNMODIFIED,
    };
  });
};

export const getAllPriceFFTags = async (): Promise<PriceffTag[]> => {
  const client = getApiClient();
  const data = await client.getPriceffTags();
  return data.map((tag, index) => {
    return {
      ...tag,
      color: dynamicPriceAreaColors[index] ?? '#FF0000',
    };
  });
};

export const getDeliveryZonesForRestaurant = async (restaurantId: string) => {
  const client = getApiClient();
  const data = await client.getAllDeliveryZonesForShop(restaurantId);
  if (!data.deliveryZones) {
    return [];
  }

  return data.deliveryZones;
};

export const getDeliveryZonesForRestaurants = async (shopIds: string[]) => {
  const promises = shopIds.map((id) => getDeliveryZonesForRestaurant(id));
  const responses = await Promise.all(promises);
  return uniqBy(responses.flat(), 'id');
};

export const getRestaurantsForDeliveryZone = async (
  deliveryZoneId: number
): Promise<RestaurantWithNightSaleDetailsDto[]> => {
  const client = getApiClient();
  const data = await client.getDeliveryZoneRestaurants(deliveryZoneId);
  return data;
};

export const getRestaurantsForDeliveryZones = async (
  deliveryZoneIds: number[]
): Promise<RestaurantWithNightSaleDetailsDto[]> => {
  const client = getApiClient();
  const promises = deliveryZoneIds.map((id) => client.getDeliveryZoneRestaurants(id));
  const responses = await Promise.all(promises);
  return uniqBy(responses.flat(), 'externalId');
};

export const updateDeliveryZone = (deliveryZoneId: number, request: UpdateDeliveryZoneRequest) => {
  const client = getApiClient();
  postToSlack(`Delivery zone V2 ${request.deliveryZoneName} (${deliveryZoneId}) updated`, 'restaurant-updates');
  return client.updateDeliveryZone(deliveryZoneId, request);
};

export const deleteDeliveryZone = (deliveryZoneId: number) => {
  const client = getApiClient();
  postToSlack(`Delivery zone V2 ${deliveryZoneId} deleted`, 'restaurant-updates');
  return client.deleteDeliveryZone(deliveryZoneId);
};

export const saveDeliveryZones = async (deliveryZones: DeliveryZone[]): Promise<unknown> => {
  const promises: Promise<void>[] = [];

  try {
    for (const deliveryZone of deliveryZones) {
      if (deliveryZone.state === deliveryZoneState.DELETED && deliveryZone.id) {
        promises.push(deleteDeliveryZone(deliveryZone.id));
      }

      if (deliveryZone.state === deliveryZoneState.EDITED && deliveryZone.id) {
        promises.push(updateDeliveryZone(deliveryZone.id, getUpdatedDeliveryZone(deliveryZone)));
      }
    }

    await Promise.all(promises);
  } catch {
    return true; // Handle error when client is fixed
  }
};

export const linkRestaurantToZone = async (deliveryZoneId: number, shopId: number, isNightSaleOnly: boolean) => {
  const client = getApiClient();
  postToSlack(`Restaurant ${shopId} linked to delivery zone ${deliveryZoneId}`, 'restaurant-updates');
  await client.linkShopToDeliveryZone(deliveryZoneId, { shopId, isNightSaleOnly });
};

export const unlinkRestaurantFromZone = async (deliveryZoneId: number, shopId: number) => {
  const client = getApiClient();
  postToSlack(`Restaurant ${shopId} unlinked from delivery zone ${deliveryZoneId}`, 'restaurant-updates');
  await client.deleteDeliveryZoneShop(deliveryZoneId, shopId);
};

export interface DeliveryZone extends DeliveryZoneDto {
  zone: {
    coordinates: Point[][];
  };
  state: deliveryZoneState;
}

export const getUpdatedDeliveryZone = (deliveryZone: DeliveryZone): UpdateDeliveryZoneRequest => {
  return {
    deliveryFeeType: deliveryZone.deliveryFeeType,
    fixedDeliveryFee: deliveryZone.fixedDeliveryFee,
    priceffTagId: deliveryZone.priceffTagId,
    deliveryZoneName: deliveryZone.deliveryZoneName ?? '',
    polygon: deliveryZone.zone,
    boundingBox: {
      minLatitude: deliveryZone.minLatitude,
      maxLatitude: deliveryZone.maxLatitude,
      minLongitude: deliveryZone.minLongitude,
      maxLongitude: deliveryZone.maxLongitude,
    },
    priority: 1,
  };
};

export const sendRestaurantDeliveryZoneToWolt = async (
  shopId: number,
  ccEmail: string,
  filename: string,
  type: string,
  data: string
) => {
  const client = getApiClient();
  const request: SendRestaurantDeliveryZoneToExternalSystemsRequest = {
    shopId: shopId,
    ccEmail: ccEmail,
    deliveryZoneAttachment: {
      data: data,
      filename: filename,
      type: type,
    },
  };
  postToSlack(`Restaurant ${shopId} delivery zones sent to Wolt and Foodora`, 'restaurant-updates');
  await client.sendRestaurantDeliveryZoneToExternalSystems(request);
};

export const getUserShopsApiRoles = async (): Promise<UserRolesDto> => {
  const client = getApiClient();
  return await client.getUserDetails();
};

/**
 * Public Holiday Pricing Dates
 */
export const upsertPublicHolidayPricingDate = async (exceptionDate: PublicHolidayPricingDate): Promise<void> => {
  const client = getApiClient();

  if (!exceptionDate.id) {
    await postToSlack(`Exception date ${exceptionDate.name} created`, 'restaurant-updates');
    await client.createPublicHolidayPricingDate(exceptionDate);
  } else {
    await postToSlack(`Exception date ${exceptionDate.name} updated`, 'restaurant-updates');
    await client.updatePublicHolidayPricingDate(exceptionDate);
  }
};

const getAllPublicHolidayPricingDates = async (): Promise<PublicHolidayPricingDate[]> => {
  const client = getApiClient();

  const data = await client.getAllPublicHolidayPricingDates();

  return data.map((exceptionDate) => ({
    ...exceptionDate,
    date: dayjs(exceptionDate.date).format(DatePickerFormat),
  }));
};

export const updateRestaurantSlug = async (shopExternalId: string, slug: string): Promise<void> => {
  const client = getApiClient();
  return client.updateShopSlug(shopExternalId, { slug });
};

export const removePublicHolidayPricingDate = async (id: number): Promise<void> => {
  const client = getApiClient();
  await postToSlack(`Exception date ${id} removed`, 'restaurant-updates');
  await client.deletePublicHolidayPricingDate(id);
};
export const useAllPublicHolidayPricingDates = (
  options = getDefaultOptions<PublicHolidayPricingDate[]>()
): UseQueryResult<PublicHolidayPricingDate[], unknown> =>
  useQuery<PublicHolidayPricingDate[]>([shopsApiQueryKeys.allExceptionDates], getAllPublicHolidayPricingDates, options);

export const getEcomSalesReport = async (
  startDate: string,
  endDate: string,
  signal?: AbortSignal
): Promise<EcomSalesReportResponse> => {
  const client = getApiClient(ECOM_SALES_REPORT_TIMEOUT_MS);

  const request: EcomSalesReportRequest = {
    startDate: startDate,
    endDate: endDate,
  };

  return await client.getEcomSalesReport(request, signal);
};

// Wraps the slow ecom-sales report in react-query so it gets caching and dedup, and so
// switching periods cancels the in-flight request rather than racing stale responses.
export const useEcomSalesReport = (
  startDate: string,
  endDate: string,
  options = getDefaultOptions<EcomSalesReportResponse>()
): UseQueryResult<EcomSalesReportResponse, unknown> =>
  useQuery<EcomSalesReportResponse>(
    [shopsApiQueryKeys.ecomSalesReport, startDate, endDate],
    ({ signal }) => getEcomSalesReport(startDate, endDate, signal),
    options
  );

export const getEcomLiveSalesReport = async (signal?: AbortSignal): Promise<EcomLiveSalesReportResponse> => {
  // Reuses the long-timeout client: this current-day aggregate can be as slow as the historical report.
  const client = getApiClient(ECOM_SALES_REPORT_TIMEOUT_MS);
  return await client.getEcomLiveSalesReport(signal);
};

// Live current-day report. Unlike the historical report it self-refreshes: refetchInterval polls the
// backend every minute, but only while the tab is visible (refetchIntervalInBackground: false), and it
// refetches immediately when the user returns to the tab (refetchOnWindowFocus: true).
export const useEcomLiveSalesReport = (
  options: Options<EcomLiveSalesReportResponse> = {
    ...getDefaultOptions<EcomLiveSalesReportResponse>(),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  }
): UseQueryResult<EcomLiveSalesReportResponse, unknown> =>
  useQuery<EcomLiveSalesReportResponse>(
    [shopsApiQueryKeys.ecomLiveSalesReport],
    ({ signal }) => getEcomLiveSalesReport(signal),
    options
  );

const useShopsMutation = <TData = unknown, TVariables = void>(
  mutationFn: MutationFunction<TData, TVariables>,
  options: Omit<ApiMutationOptions<TData, TVariables>, 'getError'>
) => useApiMutation(mutationFn, { getError: getShopsApiError, ...options });

export const useUpdateRestaurantSlug = (shopExternalId: string, ui?: ApiMutationUiOptions<unknown, string>) =>
  useShopsMutation((slug: string) => updateRestaurantSlug(shopExternalId, slug), {
    invalidateKeys: [[shopsApiQueryKeys.allRestaurants]],
    ...ui,
  });

export const useLinkLegalEntity = (shopExternalId: string, ui?: ApiMutationUiOptions<unknown, number>) =>
  useShopsMutation((legalEntityId: number) => linkRestaurantToLegalEntity(shopExternalId, legalEntityId), {
    invalidateKeys: [[shopsApiQueryKeys.restaurantLegalEntity, shopExternalId]],
    ...ui,
  });

type SaveLegalEntityVariables = { legalEntityId?: number; request: CreateLegalEntityRequest };

/** Updates when legalEntityId is given, creates otherwise. */
export const useSaveLegalEntity = (ui?: ApiMutationUiOptions<unknown, SaveLegalEntityVariables>) =>
  useShopsMutation(
    ({ legalEntityId, request }: SaveLegalEntityVariables) =>
      legalEntityId ? updateLegalEntity(legalEntityId, request) : createLegalEntity(request),
    { invalidateKeys: [[shopsApiQueryKeys.allLegalEntities]], ...ui }
  );

type SaveGlobalExceptionHoursVariables = { id?: number; request: CreateGlobalOpeningHoursExceptionRequest };

/** Updates when id is given, creates otherwise. */
export const useSaveGlobalExceptionHours = (ui?: ApiMutationUiOptions<unknown, SaveGlobalExceptionHoursVariables>) =>
  useShopsMutation(
    ({ id, request }: SaveGlobalExceptionHoursVariables) =>
      id ? updateGlobalExceptionHours({ ...request, id }) : addGlobalExceptionHours(request),
    { invalidateKeys: [[shopsApiQueryKeys.globalExceptionHours]], ...ui }
  );

export const useDeleteGlobalExceptionHours = (ui?: ApiMutationUiOptions<unknown, number>) =>
  useShopsMutation((id: number) => deleteGlobalExceptionHours({ id }), {
    invalidateKeys: [[shopsApiQueryKeys.globalExceptionHours]],
    ...ui,
  });

export const useUpsertOpeningHours = (
  shopExternalId: string,
  ui?: ApiMutationUiOptions<unknown, UpsertOpeningHoursRequest[]>
) =>
  useShopsMutation((requests: UpsertOpeningHoursRequest[]) => Promise.all(requests.map(updateOpeningHours)), {
    invalidateKeys: [[shopsApiQueryKeys.singleRestaurantHours, shopExternalId]],
    ...ui,
  });

export const useAddOpeningHoursException = (
  shopExternalId: string,
  ui?: ApiMutationUiOptions<unknown, CreateOpeningHoursExceptionRequest>
) =>
  useShopsMutation(addRestaurantOpeningHoursException, {
    invalidateKeys: [[shopsApiQueryKeys.singleRestaurantExceptionHours, shopExternalId]],
    ...ui,
  });

export const useDeleteOpeningHoursException = (shopExternalId: string, ui?: ApiMutationUiOptions<unknown, number>) =>
  useShopsMutation((id: number) => deleteRestaurantOpeningHoursException({ id }), {
    invalidateKeys: [[shopsApiQueryKeys.singleRestaurantExceptionHours, shopExternalId]],
    ...ui,
  });

type UpdateRestaurantStatusVariables = UpsertRestaurantStatusRequest | UpdateRestaurantTemporaryStatusRequest;

/** Sets the temporary status when the request has temporaryStatus, the normal status otherwise. */
export const useUpdateRestaurantStatus = (
  shopExternalId: string,
  ui?: ApiMutationUiOptions<unknown, UpdateRestaurantStatusVariables>
) =>
  useShopsMutation(
    (request: UpdateRestaurantStatusVariables) =>
      'temporaryStatus' in request ? updateRestaurantTemporaryStatus(request) : updateRestaurantStatus(request),
    { invalidateKeys: [[shopsApiQueryKeys.singleRestaurantStatus, shopExternalId]], ...ui }
  );

const dynamicPriceAreaColors = [
  '#A1DEF0',
  '#2A586A',
  '#4D9CAA',
  '#21F0B6',
  '#097B35',
  '#88DC40',
  '#683D0D',
  '#F4BB8F',
  '#821F48',
  '#E5196A',
  '#E26D67',
  '#B1BF81',
  '#6F7D43',
  '#F4D403',
  '#AB7950',
  '#FB7810',
  '#9A2A06',
  '#2CF52B',
  '#323D96',
  '#AAA4E1',
  '#8C0DA4',
  '#A477FB',
  '#E84FE1',
  '#7212FF',
  '#FE2B27',
];
